import { useState } from 'react';
import {
  CheckCheckIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloudDownloadIcon,
  EyeIcon,
  HandHelpingIcon,
  MessageSquareShareIcon,
} from 'lucide-react';
import { TL, TlKaart, TlKnop, TlTerug, TlWrap } from './chrome.js';

/**
 * Twee schermen uit het nagebouwde Therapieland-zorgverlenerportaal:
 * de programmavoortgang van een patiënt en de antwoorden op één stap.
 *
 * Nagebouwd naar docs/designs/screenshots-therapieland/
 * patient-overzicht-programma-voortgang-therapieland.png en
 * antwoorden-patient-op-vragenlijst-therapieland.png.
 *
 * In die screenshots staat een echte patiëntnaam; die is hier vervangen door de
 * prop patientNaam met een fictieve demopatiënt. Alle overige data is hardcoded
 * en verzonnen; er zijn geen netwerkcalls.
 */

/** Het enige element buiten het TL-palet: kleuren gemeten in screenshot 1. */
const PROGRAMMA_ICOON = { vulling: '#afe47c', rand: '#971c1c' };

interface TlOnderdeel {
  titel: string;
  /** Bepaalt zowel het aantal stippen als de breedte van de voortgangsbalk. */
  stappen: number;
}

const ONDERDELEN: TlOnderdeel[] = [
  { titel: 'Van Start!', stappen: 8 },
  { titel: 'Accepteren van gevoel', stappen: 6 },
  { titel: 'Accepteren van situatie', stappen: 7 },
  { titel: 'Jezelf accepteren', stappen: 7 },
];

export function TlVoortgang({
  patientNaam,
  programma,
  onTerug,
  onStap,
}: {
  patientNaam: string;
  programma: string;
  onTerug: () => void;
  onStap: () => void;
}): JSX.Element {
  return (
    <TlWrap>
      <TlTerug label="Werkmap" onClick={onTerug} />

      <div className="mt-6 flex items-center gap-5">
        <span
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: PROGRAMMA_ICOON.vulling,
            border: `2px solid ${PROGRAMMA_ICOON.rand}`,
          }}
        >
          <HandHelpingIcon size={32} strokeWidth={1.8} className="text-white" />
        </span>
        <h1 className="text-[30px] font-bold" style={{ color: TL.ink }}>
          {programma}
        </h1>
      </div>

      <div className="mt-7 px-5 pb-7 pt-6" style={{ backgroundColor: TL.panel }}>
        <div className="flex items-center justify-between">
          <span className="text-[16px] font-bold" style={{ color: TL.ink }}>
            Voortgang van {patientNaam}
          </span>
          <button
            type="button"
            className="flex items-center gap-2.5 text-[15px]"
            style={{ color: TL.primair }}
          >
            <CloudDownloadIcon size={22} strokeWidth={1.8} /> Download voortgang
          </button>
        </div>

        <div className="mt-11 flex items-start gap-1">
          {ONDERDELEN.map((o) => (
            <div key={o.titel} style={{ flexGrow: o.stappen, flexBasis: 0 }}>
              <TlVoortgangBalk stappen={o.stappen} />
              <span className="mt-1.5 block text-[11.5px]" style={{ color: TL.zacht }}>
                {o.titel}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="text-[26px] font-bold" style={{ color: TL.ink }}>
          Programma inhoud
        </h2>
        <div className="flex items-center gap-5">
          <button
            type="button"
            className="flex items-center gap-1.5 text-[12.5px] font-semibold tracking-wide"
            style={{ color: TL.body }}
          >
            ALLES UITKLAPPEN <ChevronDownIcon size={16} />
          </button>
          <TlKnop variant="omlijnd">Toon alle antwoorden</TlKnop>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-2.5">
        {ONDERDELEN.map((o) => (
          <TlOnderdeelBalk key={o.titel} titel={o.titel} onKies={onStap} />
        ))}
      </div>
    </TlWrap>
  );
}

/** Groene balk met een donkere stip op elke overgang tussen twee stappen. */
function TlVoortgangBalk({ stappen }: { stappen: number }): JSX.Element {
  return (
    <div className="flex h-5 items-center" style={{ backgroundColor: TL.groenBalk }}>
      {Array.from({ length: stappen }, (_, i) => (
        <span key={i} className="flex flex-1 items-center justify-end">
          {i < stappen - 1 && (
            <span
              className="h-[7px] w-[7px] translate-x-1/2 rounded-full"
              style={{ backgroundColor: TL.groenStip }}
            />
          )}
        </span>
      ))}
    </div>
  );
}

function TlOnderdeelBalk({ titel, onKies }: { titel: string; onKies: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onKies}
      className="flex h-[65px] items-center gap-4 rounded px-5 text-left"
      style={{ backgroundColor: TL.stapRij, border: `1px solid ${TL.rand}` }}
    >
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: TL.groenBalk }}
      >
        <CheckIcon size={13} strokeWidth={3.5} className="text-white" />
      </span>

      <span className="flex-1 text-[16px] font-semibold" style={{ color: TL.ink }}>
        {titel}
      </span>

      <span className="flex items-center gap-2.5 text-[14.5px]" style={{ color: TL.ink }}>
        <CheckCheckIcon size={18} strokeWidth={1.8} /> Markeer alles
      </span>
      <span className="flex items-center gap-2.5 text-[14.5px]" style={{ color: TL.zacht }}>
        <EyeIcon size={18} strokeWidth={1.8} /> Verberg alles
      </span>
      <ChevronDownIcon size={20} style={{ color: TL.ink }} />
    </button>
  );
}

const VRAGEN = [
  { id: 'v1', tekst: 'Zeg je vaak ja tegen verzoeken, ook als je eigenlijk nee wilt zeggen?' },
  {
    id: 'v2',
    tekst: 'Vind je het moeilijk om je mening te geven als anderen het niet met je eens zijn?',
  },
  {
    id: 'v3',
    tekst:
      'Geef je anderen vaak voorrang bij beslissingen, zelfs als jij daar nadeel door kan krijgen?',
  },
];

const INTRO =
  'In deze stap sta je stil bij de reden dat je dit programma bent gaan volgen en wat je graag zou willen bereiken. Laten we beginnen met een paar vragen:';

// Losse constante omdat de enkele aanhalingstekens in JSX-tekst niet mogen.
const UITKOMST =
  "Heb je op een of meerdere vragen 'Ja' beantwoord? Dan is het aan te raden dit programma verder te doorlopen!";

const ANTWOORD =
  'Ik merk dat ik op mijn werk vaak taken van anderen overneem en daarna te weinig tijd overhoud voor mijn eigen werk. Ik wil leren om rustig aan te geven waar mijn grens ligt.';

export function TlVragenlijst({ onTerug }: { onTerug: () => void }): JSX.Element {
  const [keuzes, setKeuzes] = useState<Record<string, string>>({ v1: 'Ja', v2: 'Ja', v3: 'Ja' });

  return (
    <TlWrap>
      <TlTerug label="Opkomen voor jezelf" onClick={onTerug} />

      <div className="mt-8 grid grid-cols-[1fr_252px] items-start gap-7">
        <div>
          <h1 className="text-[30px] font-bold" style={{ color: TL.ink }}>
            Opkomen voor jezelf
          </h1>

          <button
            type="button"
            className="mt-4 flex items-center gap-2.5 text-[14.5px] font-semibold"
            style={{ color: TL.primair }}
          >
            <MessageSquareShareIcon size={20} strokeWidth={1.8} /> Stuur een bericht naar je patiënt
            over deze stap
          </button>

          <h2 className="mt-9 text-[18px] font-bold" style={{ color: TL.ink }}>
            Hoe zit het bij jou?
          </h2>
          <p className="mt-4 text-[15.5px] leading-[1.65]" style={{ color: TL.body }}>
            {INTRO}
          </p>

          {VRAGEN.map((v) => (
            <div key={v.id} className="mt-8">
              <p className="text-[15px] font-bold" style={{ color: TL.ink }}>
                {v.tekst}
              </p>
              <div className="mt-3 flex flex-col gap-3">
                {['Ja', 'Nee'].map((optie) => (
                  <TlRadio
                    key={optie}
                    naam={v.id}
                    label={optie}
                    aan={keuzes[v.id] === optie}
                    onKies={() => setKeuzes({ ...keuzes, [v.id]: optie })}
                  />
                ))}
              </div>
            </div>
          ))}

          <p className="mt-11 text-[15.5px] leading-[1.65]" style={{ color: TL.body }}>
            {UITKOMST}
          </p>

          <p className="mt-7 text-[15px] font-bold" style={{ color: TL.ink }}>
            Wat is voor jou de reden dat je dit programma bent gaan volgen?
          </p>
          <p className="mt-4 text-[15.5px]" style={{ color: TL.body }}>
            Je kunt hierbij ook denken aan een situatie die je onlangs hebt meegemaakt.
          </p>

          <div
            className="mt-5 rounded px-5 py-4 text-[15.5px] leading-[1.65]"
            style={{ backgroundColor: TL.panel, border: `1px solid ${TL.zacht}`, color: TL.body }}
          >
            {ANTWOORD}
          </div>
        </div>

        <div>
          <h2 className="text-[17px] font-bold" style={{ color: TL.ink }}>
            Programma overzicht
          </h2>
          <TlKaart className="mt-4">
            <div className="px-4 py-4" style={{ backgroundColor: TL.panel }}>
              <span className="text-[14px]" style={{ color: TL.body }}>
                10% Afgerond
              </span>
              <div
                className="mt-2 h-2 overflow-hidden rounded-full"
                style={{ border: `1px solid ${TL.muted}` }}
              >
                <div className="h-full" style={{ width: '10%', backgroundColor: TL.groenBalk }} />
              </div>

              <div className="mt-4">
                <TlSessieKop titel="Sessie 1: Van start!" status="bezig" open />
                {SESSIE1.map((s) => (
                  <TlSessieItem key={s.titel} titel={s.titel} status={s.status} huidig={s.huidig} />
                ))}
                {SESSIES.map((titel) => (
                  <TlSessieKop key={titel} titel={titel} status="open" open={false} />
                ))}
              </div>
            </div>
          </TlKaart>
        </div>
      </div>
    </TlWrap>
  );
}

type TlStatus = 'klaar' | 'bezig' | 'open';

const SESSIE1: { titel: string; status: TlStatus; huidig: boolean }[] = [
  { titel: 'Van start!', status: 'klaar', huidig: false },
  { titel: 'Hoe zit het bij jou?', status: 'klaar', huidig: true },
  { titel: 'Wat zijn jouw doelen?', status: 'bezig', huidig: false },
  { titel: 'De eerste stappen zetten', status: 'open', huidig: false },
  { titel: 'Wat betekent opkomen voor jezelf?', status: 'open', huidig: false },
];

const SESSIES = [
  'Sessie 2: Assertief gedrag',
  'Sessie 3: Compassie voor jezelf',
  'Sessie 4: Jezelf verder uitdagen',
  'Sessie 5: Terugblik en toekomst',
];

function TlSessieKop({
  titel,
  status,
  open,
}: {
  titel: string;
  status: TlStatus;
  open: boolean;
}): JSX.Element {
  return (
    <button type="button" className="flex w-full items-start gap-3 py-2 text-left">
      <TlStatusIcoon status={status} />
      <span className="flex-1 text-[15px] font-bold leading-[1.5]" style={{ color: TL.ink }}>
        {titel}
      </span>
      {open ? (
        <ChevronUpIcon size={18} className="mt-0.5 shrink-0" style={{ color: TL.ink }} />
      ) : (
        <ChevronDownIcon size={18} className="mt-0.5 shrink-0" style={{ color: TL.ink }} />
      )}
    </button>
  );
}

function TlSessieItem({
  titel,
  status,
  huidig,
}: {
  titel: string;
  status: TlStatus;
  huidig: boolean;
}): JSX.Element {
  return (
    <button type="button" className="flex w-full items-start gap-3 py-2 text-left">
      <TlStatusIcoon status={status} />
      <span
        className="flex-1 text-[14px] leading-[1.5]"
        style={{
          color: huidig ? TL.ink : TL.body,
          fontWeight: huidig ? 600 : 400,
          textDecoration: huidig ? 'underline' : undefined,
        }}
      >
        {titel}
      </span>
    </button>
  );
}

function TlStatusIcoon({ status }: { status: TlStatus }): JSX.Element {
  if (status === 'klaar') {
    return (
      <span
        className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: TL.groenBalk }}
      >
        <CheckIcon size={12} strokeWidth={3.5} className="text-white" />
      </span>
    );
  }

  const rand = status === 'bezig' ? TL.primair : TL.muted;
  return (
    <svg viewBox="0 0 18 18" className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden="true">
      <circle cx="9" cy="9" r="8" fill="none" stroke={rand} strokeWidth="2" />
      {status === 'bezig' && <path d="M9 1 A8 8 0 0 0 9 17 Z" fill={TL.primair} />}
    </svg>
  );
}

function TlRadio({
  naam,
  label,
  aan,
  onKies,
}: {
  naam: string;
  label: string;
  aan: boolean;
  onKies: () => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-3.5">
      <input type="radio" name={naam} checked={aan} onChange={onKies} className="sr-only" />
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
        style={{ border: `1px solid ${aan ? TL.primair : TL.muted}` }}
      >
        {aan && (
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TL.primair }} />
        )}
      </span>
      <span className="text-[15px]" style={{ color: TL.body }}>
        {label}
      </span>
    </label>
  );
}
